import React, { useState } from 'react';
import { ChevronDown, ExternalLink, Loader2, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatRescheduledForDisplay } from '@/utils/fivetranSyncStatus';
import { CsvFile, FeedConnection, connectorIdOf, formatFeedDate, latestDate } from '@/shared/merchantFeeds';
import type { StatusIndicator } from '@/hooks/useMerchantConnections';
import { describeLastSync } from '@/shared/connectionSync';
import type { LastSync } from '@/shared/types/connectionSync';
import { cn } from '@/lib/utils';

// Rows for Settings › Pricing Data In › Data Feeds (FIG-321 / REQ-042, REQ-054).
// The actions (settings, View in Fivetran, remove) are the ones the Overview
// tab's Data Connections card had; that card is removed.

const StatusDot: React.FC<{ status: StatusIndicator }> = ({ status }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        tabIndex={0}
        role="img"
        aria-label={status.label}
        className={cn('h-2.5 w-2.5 flex-none rounded-full', status.color)}
      />
    </TooltipTrigger>
    <TooltipContent className="max-w-xs">
      <p className="font-medium">{status.label}</p>
      {status.detail ? <p className="mt-1 text-xs text-muted-foreground">{status.detail}</p> : null}
      {status.rescheduledFor && !status.detail?.includes('Next sync') ? (
        <p className="mt-1 text-xs">{formatRescheduledForDisplay(status.rescheduledFor) ?? status.rescheduledFor}</p>
      ) : null}
    </TooltipContent>
  </Tooltip>
);

interface FeedRowProps {
  feed: FeedConnection;
  detail: string;
  status: StatusIndicator;
  /** Last successful sync (FIG-345 UC-231); null while loading or unknown. */
  lastSync?: LastSync | null;
  /** The list's clock, so the relative time advances; defaults to render time. */
  now?: Date;
  canEdit: boolean;
  inherited: boolean;
  removing: boolean;
  /** Another removal is running or still refreshing. */
  locked?: boolean;
  /** Live (Fivetran) connections get Connection Settings and View in Fivetran. */
  isFivetran: boolean;
  onOpenSettings: () => void;
  onRemove: () => Promise<void>;
}

export const FeedRow: React.FC<FeedRowProps> = ({
  feed,
  detail,
  status,
  lastSync = null,
  now,
  canEdit,
  inherited,
  removing,
  locked = false,
  isFivetran,
  onOpenSettings,
  onRemove,
}) => {
  const [confirming, setConfirming] = useState(false);
  const connectorId = connectorIdOf(feed.key, feed.value);
  const lastSyncDisplay = describeLastSync(lastSync, now);
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2" data-testid={`feed-row-${feed.key}`}>
      <StatusDot status={status} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium" title={feed.name}>{feed.name}</div>
        <div className="text-xs text-muted-foreground">{detail}</div>
        {lastSyncDisplay && (
          <div className="text-xs text-muted-foreground" title={lastSyncDisplay.title ?? undefined} data-testid={`last-sync-${feed.key}`}>
            {lastSyncDisplay.text}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        {isFivetran && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={`Connection settings for ${feed.name}`} onClick={onOpenSettings}>
                  <Settings className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Connection Settings</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 text-xs text-blue-600 hover:text-blue-800" asChild>
                  <a
                    href={`https://fivetran.com/dashboard/connections/${connectorId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View ${feed.name} in Fivetran`}
                  >
                    <span>View in Fivetran</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Connection ID: figabl_{feed.value.connectionId}</p></TooltipContent>
            </Tooltip>
          </>
        )}
        {canEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Disabled buttons get no pointer events, so the tooltip sits on a wrapper. */}
              <span tabIndex={inherited ? 0 : -1} className="inline-flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove ${feed.name} connection`}
                  disabled={removing || inherited || locked}
                  onClick={() => setConfirming(true)}
                >
                  {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{inherited ? 'Cannot remove connections inherited from source organisation' : 'Remove Connection'}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {feed.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently disconnect this data source and delete its associated database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removing}
              onClick={async () => {
                await onRemove();
                setConfirming(false);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
};

interface CsvSummaryRowProps {
  kind: CsvFile['kind'];
  files: CsvFile[];
  canEdit: boolean;
  removingId: string | null;
  /** Another removal is running or still refreshing. */
  busy?: boolean;
  isInherited: (file: CsvFile) => boolean;
  onRemoveFile: (file: CsvFile) => Promise<void>;
}

/** One summary row per kind of CSV upload, with the files listed on demand (REQ-054). */
export const CsvSummaryRow: React.FC<CsvSummaryRowProps> = ({ kind, files, canEdit, removingId, busy = false, isInherited, onRemoveFile }) => {
  const [showFiles, setShowFiles] = useState(false);
  const [confirmFile, setConfirmFile] = useState<CsvFile | null>(null);
  const name = { inventory: 'Inventory CSV uploads', sales: 'Sales transaction CSV uploads', other: 'Banking and accounting CSV uploads' }[kind];
  const count = `${files.length} ${files.length === 1 ? 'file' : 'files'}`;

  let detail: string;
  if (files.length === 0) {
    detail = kind === 'inventory' ? 'No inventory CSV files uploaded' : kind === 'sales' ? 'No sales transaction CSV files uploaded' : 'No banking or accounting CSV files uploaded';
  } else if (kind === 'inventory') {
    const binding = latestDate(files.map((f) => f.bindingDate));
    detail = [binding ? `Latest binding date ${formatFeedDate(binding)}` : null, count].filter(Boolean).join(' · ');
  } else {
    const uploaded = latestDate(files.map((f) => f.uploadedAt));
    detail = [uploaded ? `Latest upload ${formatFeedDate(uploaded)}` : null, count].filter(Boolean).join(' · ');
  }

  const fileId = (file: CsvFile) => file.importId ?? file.connectionKey;

  return (
    <li className="rounded-md border px-3 py-2" data-testid={`csv-summary-${kind}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className={cn('h-2.5 w-2.5 flex-none rounded-full', files.length > 0 ? 'bg-green-500' : 'bg-gray-300')}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{name}</div>
          <div className="text-xs text-muted-foreground">{detail}</div>
        </div>
        {files.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            aria-expanded={showFiles}
            onClick={() => setShowFiles((v) => !v)}
          >
            {showFiles ? 'Hide files' : 'Show files'}
            <ChevronDown className={cn('h-3 w-3 transition-transform', showFiles && 'rotate-180')} />
          </Button>
        )}
      </div>
      {showFiles && (
        <ul className="mt-2 space-y-1 border-t pt-2" aria-label={`${name} files`}>
          {files.map((file) => {
            const id = fileId(file);
            const inherited = isInherited(file);
            return (
              <li key={id} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate" title={file.fileName}>{file.fileName}</span>
                <span className="text-xs text-muted-foreground">
                  {formatFeedDate(file.kind === 'inventory' ? file.bindingDate ?? file.uploadedAt : file.uploadedAt)}
                </span>
                {canEdit && !inherited && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Remove ${file.fileName}`}
                    disabled={busy || removingId === id}
                    onClick={() => setConfirmFile(file)}
                  >
                    {removingId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <AlertDialog open={!!confirmFile} onOpenChange={(open) => !open && setConfirmFile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmFile?.fileName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this imported file from the connection. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!removingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!!removingId}
              onClick={async () => {
                if (confirmFile) await onRemoveFile(confirmFile);
                setConfirmFile(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
};
